import * as React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type OnChangeFn,
} from "@tanstack/react-table";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useT } from "@/app/localization";

interface DataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
  enableSorting?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  getRowId?: (row: TData) => string;
  onRowClick?: (row: TData) => void;
  /** Double-click a row to open its full detail page/overlay. Single click
   *  (onRowClick) still opens the side panel; the two are complementary. */
  onRowDoubleClick?: (row: TData) => void;
  selectedRowId?: string;
  rowClassName?: (row: TData) => string;
  isLoading?: boolean;
  emptyMessage?: string;
  className?: string;
  tableClassName?: string;
  stickyHeader?: boolean;
  footer?: React.ReactNode;
}

export function DataTable<TData>({
  columns,
  data,
  sorting,
  onSortingChange,
  manualSorting,
  enableSorting = false,
  columnVisibility,
  onColumnVisibilityChange,
  getRowId,
  onRowClick,
  onRowDoubleClick,
  selectedRowId,
  rowClassName,
  isLoading,
  emptyMessage,
  className,
  tableClassName,
  stickyHeader = true,
  footer,
}: DataTableProps<TData>) {
  const t = useT();
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting
      ? {
          getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
          onSortingChange,
          manualSorting,
          state: {
            sorting: sorting ?? [],
            ...(columnVisibility ? { columnVisibility } : {}),
          },
        }
      : {
          state: {
            ...(columnVisibility ? { columnVisibility } : {}),
          },
        }),
    ...(onColumnVisibilityChange ? { onColumnVisibilityChange } : {}),
    ...(getRowId ? { getRowId } : {}),
    enableSorting,
  });

  const colCount = table.getVisibleFlatColumns().length;

  return (
    <div className={className}>
      <Table className={tableClassName}>
        <TableHeader className={cn(stickyHeader && "sticky top-0 bg-card z-10 shadow-[0_1px_0_var(--color-border)]")}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      canSort && "cursor-pointer select-none",
                    )}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                  >
                    <span className="inline-flex items-center gap-1">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && sorted === "asc" && <span className="text-xs">▲</span>}
                      {canSort && sorted === "desc" && <span className="text-xs">▼</span>}
                    </span>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: colCount }).map((_, j) => (
                  <TableCell key={j}>
                    <Skeleton className="h-4 w-full rounded-md" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colCount} className="text-center py-10 opacity-60">
                {emptyMessage ?? t("common.state.no_results")}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => {
              const isSelected = selectedRowId != null && row.id === selectedRowId;
              return (
                <TableRow
                  key={row.id}
                  className={cn(
                    (onRowClick || onRowDoubleClick) && "cursor-pointer hover:bg-muted/60 transition-colors",
                    // Stop the text-selection flash a fast double-click leaves behind.
                    onRowDoubleClick && "select-none",
                    isSelected && "bg-primary/10",
                    rowClassName?.(row.original),
                  )}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {footer}
    </div>
  );
}
